import type { ReactNode } from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Business as BusinessIcon,
  CreditCard as CreditCardIcon,
  EventAvailable as EventAvailableIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

interface SettingsShellProps {
  title: string;
  eyebrow?: string;
  children: ReactNode;
}

const settingsSections = [
  {
    label: 'General Settings',
    path: '/settings/general',
    icon: <SettingsIcon fontSize="small" />,
  },
  {
    label: 'Booking Settings',
    path: '/settings/booking',
    icon: <EventAvailableIcon fontSize="small" />,
  },
  {
    label: 'Business Logic',
    path: '/settings/business',
    icon: <BusinessIcon fontSize="small" />,
  },
  {
    label: 'Payment Settings',
    path: '/settings/payment',
    icon: <CreditCardIcon fontSize="small" />,
  },
];

export function SettingsShell({ title, eyebrow = 'Settings', children }: SettingsShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: { xs: 2, md: 4 } }}>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 2, fontWeight: 700 }}
            >
              {eyebrow}
            </Typography>
            <Typography variant="h4" fontWeight={800}>
              {title}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '260px minmax(0, 1fr)' },
              gap: 3,
              alignItems: 'start',
            }}
          >
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack direction={isMobile ? 'row' : 'column'} spacing={1} sx={{ overflowX: 'auto' }}>
                {settingsSections.map((section) => {
                  const selected = location.pathname.startsWith(section.path);
                  return (
                    <Button
                      key={section.path}
                      startIcon={section.icon}
                      onClick={() => navigate(section.path)}
                      variant={selected ? 'contained' : 'text'}
                      color={selected ? 'primary' : 'inherit'}
                      sx={{
                        justifyContent: 'flex-start',
                        minWidth: isMobile ? 180 : 'auto',
                        px: 2,
                        py: 1.25,
                        borderRadius: 1.5,
                        fontWeight: 700,
                      }}
                    >
                      {section.label}
                    </Button>
                  );
                })}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h5" fontWeight={800}>
                  {title}
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>
            </Paper>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
